import {
	Box,
	Center,
	createStyles,
	Flex,
	Group,
	Loader,
	UnstyledButton,
} from '@mantine/core';
import { Icon, IconButton, ListBox, Text } from '@repo/foundations';
import type { FormikProps } from 'formik';
import { size } from 'lodash-es';
import { useCallback, useState } from 'react';
import { useMention } from '../../../api/hooks/search';
import type { ISecodaEntity, SearchResult } from '../../../api/types';
import RelatedResourceItem from '../../EntityPageLayout/SidesheetStacks/RelatedResource/RelatedResourceItem';
import { RelatedResourcePill } from '../../InlineRelatedResources/RelatedResourcePill';
import type { TemplateFormik } from './ManageTemplatePage';

const useStyles = createStyles((theme) => ({
	wrapper: {
		flex: '1 !important',
	},
	button: {
		cursor: 'pointer',
		paddingLeft: theme.spacing.sm,
		paddingRight: theme.spacing.sm,
		paddingTop: theme.spacing['3xs'],
		paddingBottom: theme.spacing['3xs'],
		borderRadius: theme.other.borderRadius.sm,
		'&:hover': {
			backgroundColor: theme.other.getColor('surface/secondary/hover'),
		},
	},
}));

export interface RelatedResourceTemplateSelectorProps {
	formik: FormikProps<TemplateFormik>;
}

export function RelatedResourceTemplateSelector({
	formik,
}: RelatedResourceTemplateSelectorProps) {
	const { classes, theme } = useStyles();

	const [opened, setOpened] = useState<boolean>(false);
	const [searchTerm, setSearchTerm] = useState('');

	const handleSearchChange = (value: string) => {
		setSearchTerm(value);
	};

	const { isFetching, data } = useMention({
		searchTerm,
		filters: {
			operator: 'not',
			operands: [
				{
					operator: 'in',
					field: 'id',
					value:
						formik.values.related_resources?.map((r: ISecodaEntity) => r.id) ||
						[],
					operands: [],
				},
			],
		},
		options: {
			select: (entities) => entities.results,
		},
	});

	const height =
		size(data) > 6 ? theme.other.space[60] : size(data) * theme.other.space[12];

	const handleOpenChange = useCallback((newOpened: boolean) => {
		setSearchTerm('');
		setOpened(newOpened);
	}, []);

	const handleCreateResourceRelation = useCallback(
		(selected: SearchResult) => {
			formik.setFieldValue('related_resources', [
				...(formik.values.related_resources || []),
				selected,
			]);
		},
		[formik]
	);

	return (
		<Group spacing="xs" noWrap align="flex-start">
			<Text w={120} size="sm" py="4xs" color="text/secondary/default">
				Related
			</Text>
			<Group spacing="xs" className={classes.wrapper}>
				{formik.values.related_resources.length > 0 && (
					// other PropertySelector components display selected items
					// as buttons with padding. This box helps align the related items
					<Box w={theme.spacing.xs} />
				)}
				{(formik.values.related_resources ?? []).map(
					(resource: ISecodaEntity) => (
						<RelatedResourcePill
							key={resource.id}
							entity={resource}
							onRemove={async () => {
								await formik.setFieldValue(
									'related_resources',
									formik.values.related_resources.filter(
										(r: ISecodaEntity) => r.id !== resource.id
									)
								);
							}}
						/>
					)
				)}
				<ListBox opened={opened} onOpenChange={handleOpenChange} closeOnEscape>
					<ListBox.Target>
						{formik.values.related_resources.length === 0 ? (
							<UnstyledButton className={classes.button}>
								<Flex align="center" gap="3xs">
									<Icon name="plus" color="text/secondary/default" />
									<Text size="sm" color="text/secondary/default">
										Add related
									</Text>
								</Flex>
							</UnstyledButton>
						) : (
							<IconButton
								iconName="plus"
								variant="tertiary"
								tooltip="Add related resource"
							/>
						)}
					</ListBox.Target>
					<ListBox.ItemsDropdown
						usePortal
						search={{
							onChange: handleSearchChange,
							value: searchTerm,
							placeholder: 'Search entities',
						}}
						items={data}
						renderItem={(item, getProps) => (
							<RelatedResourceItem
								item={item}
								onClick={handleCreateResourceRelation}
								isViewerUser={false}
								key={item.id}
								getProps={getProps}
							/>
						)}
					>
						{isFetching && (
							<Center h={height} w={theme.other.space[60]}>
								<Loader size="sm" />
							</Center>
						)}
					</ListBox.ItemsDropdown>
				</ListBox>
			</Group>
		</Group>
	);
}
